import { useState } from 'react'
import type { Tasting } from '../../types'
import { calculateResults, getIncompleteTasters } from '../../utils/scoring'
import { calculateCorrelations } from '../../utils/correlation'
import { WineResultCard } from './WineResultCard'
import { CorrelationSummary } from './CorrelationSummary'
import { ExportButton } from './ExportButton'

type ResultsTabProps = {
  tasting: Tasting
  onAssignWine: (letterIndex: number, wineIndex: number | null) => void
  onSaveToSanity: () => void
  isSaving: boolean
  hasUnsavedChanges: boolean
}

type SortOrder = 'wine' | 'rank'

export function ResultsTab({ tasting, onAssignWine, onSaveToSanity, isSaving, hasUnsavedChanges }: ResultsTabProps) {
  const [sortOrder, setSortOrder] = useState<SortOrder>('wine')

  const results = calculateResults(tasting)
  const correlations = calculateCorrelations(tasting.tasters)
  const incompleteTasters = getIncompleteTasters(tasting)
  const wineCount = tasting.wines.length

  const sortedResults = [...results].sort((a, b) => {
    if (sortOrder === 'wine') {
      return a.index - b.index
    }
    return a.rank - b.rank || a.index - b.index
  })

  const hasTasters = tasting.tasters.length > 0

  return (
    <div className="p-4 space-y-4 max-w-2xl mx-auto">
      <div className="flex items-center gap-2">
        <span className="text-sm text-black/70">Order by</span>
        <button
          onClick={() => setSortOrder('wine')}
          className={`px-3 py-1.5 text-sm font-medium rounded transition-colors ${
            sortOrder === 'wine'
              ? 'bg-black text-cream'
              : 'bg-black/10 text-black/70 hover:bg-black/20'
          }`}
        >
          Wine
        </button>
        <button
          onClick={() => setSortOrder('rank')}
          className={`px-3 py-1.5 text-sm font-medium rounded transition-colors ${
            sortOrder === 'rank'
              ? 'bg-black text-cream'
              : 'bg-black/10 text-black/70 hover:bg-black/20'
          }`}
        >
          Rank
        </button>
      </div>

      {!hasTasters ? (
        <div className="p-3 bg-orange-100 border border-orange-400 rounded-lg text-sm">
          <span className="font-medium">(INCOMPLETE)</span> No tasters have been added yet.
        </div>
      ) : incompleteTasters.length > 0 && (
        <div className="p-3 bg-orange-100 border border-orange-400 rounded-lg text-sm">
          <span className="font-medium">(INCOMPLETE)</span> Missing scores from:{' '}
          {incompleteTasters.map((t) => t.name).join(', ')}
        </div>
      )}

      <div className="space-y-3">
        {sortedResults.map((result, i) => {
          // Find the first unassigned wine in the sorted list
          const firstUnassignedIdx = sortedResults.findIndex(
            (r) => tasting.wineAssignments[r.index] === null
          )
          // Dim cards that come after the first unassigned wine
          const dimmed = firstUnassignedIdx !== -1 && i > firstUnassignedIdx

          return (
            <WineResultCard
              key={result.index}
              result={result}
              wineCount={wineCount}
              sortOrder={sortOrder}
              wines={tasting.wines}
              wineAssignments={tasting.wineAssignments}
              onAssign={(wineIndex) => onAssignWine(result.index, wineIndex)}
              dimmed={dimmed}
            />
          )
        })}
      </div>

      <CorrelationSummary correlations={correlations} />

      <div className="pt-4 border-t border-black/10 flex items-center gap-3">
        <ExportButton tasting={tasting} />
        <button
          onClick={onSaveToSanity}
          disabled={
            isSaving ||
            !hasUnsavedChanges ||
            incompleteTasters.length > 0 ||
            !hasTasters ||
            tasting.wineAssignments.some((a) => a === null)
          }
          className="px-4 py-2 bg-black text-cream rounded hover:bg-black/80 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {isSaving ? 'Saving...' : 'Save to Sanity'}
        </button>
      </div>
    </div>
  )
}
